'use client';

import { type ReactNode, useState } from 'react';
import Link from 'next/link';
import { Video } from '@/app/elements/video/video';
import { formatRuntime } from '@/app/elements/video/video.utils';
import { SvgLoader } from '@/app/elements/svg/loader';
import { useInfluencerVideoSelected } from '@/hooks/use-influencer-video-selected';

import './styles/default.scss';

type InfluencerVideoTileProps = {
    videoId: string;
    videoSrc: string;
    poster: string;
    title: string;
    position: number;
    influencerName: string | null;
    influencerAvatar: string | null;
    influencerBackground: string | null;
    isPlaybackCandidate?: boolean;
    sessionHref: string;
    gameTile: ReactNode;
};

export const InfluencerVideoTile = ({
    videoId,
    videoSrc,
    poster,
    title,
    position,
    influencerName,
    influencerAvatar,
    influencerBackground,
    isPlaybackCandidate = false,
    sessionHref,
    gameTile
}: InfluencerVideoTileProps) => {
    const [runtime, setRuntime] = useState<string | null>(null);
    const { containerRef, handleVideoSelected } = useInfluencerVideoSelected<HTMLDivElement>({
        module: 'video_preview_carousel',
        position,
        videoTitle: title,
        pageName: 'influencer_hub'
    });

    return (
        <div className="influencer-video-tile" data-testid="influencer-video-tile" ref={containerRef}>
            <div className="influencer-video-tile__video">
                <Video
                    uid={videoId}
                    videoId={videoId}
                    src={videoSrc}
                    poster={poster}
                    isPlaybackCandidate={isPlaybackCandidate}
                    hasVideoDetails={false}
                    onDurationChange={(seconds) =>
                        Number.isFinite(seconds) && seconds > 0 && setRuntime(formatRuntime(seconds))
                    }
                />
                <Link
                    href={sessionHref}
                    className="influencer-video-tile__cover"
                    aria-label={`Open ${title || influencerName || 'gameplay session'}`}
                    prefetch={false}
                    onNavigate={handleVideoSelected}
                />
                <div className="influencer-video-tile__overlay">
                    <div className="influencer-video-tile__meta">
                        <span className="influencer-video-tile__avatar">
                            {influencerBackground && (
                                <img
                                    className="influencer-video-tile__avatar-bg"
                                    src={influencerBackground}
                                    alt=""
                                    aria-hidden="true"
                                    loading="lazy"
                                />
                            )}
                            {influencerAvatar && (
                                <img
                                    className="influencer-video-tile__avatar-logo"
                                    src={influencerAvatar}
                                    alt={influencerName ?? ''}
                                    loading="lazy"
                                />
                            )}
                        </span>
                        <div className="influencer-video-tile__meta-text">
                            {title && <p className="influencer-video-tile__title">{title}</p>}
                            {runtime && <span className="influencer-video-tile__runtime">{runtime}</span>}
                        </div>
                    </div>
                    <span className="influencer-video-tile__fullscreen" aria-hidden="true">
                        <SvgLoader iconName="fullscreen" />
                    </span>
                </div>
            </div>
            {gameTile && (
                <div className="influencer-video-tile__game" data-t-videotitle={title}>
                    {gameTile}
                </div>
            )}
        </div>
    );
};
